<script lang="ts">
  /**
   * A sentence on its own: a hint under a form, why something is
   * unavailable ("You are offline"), what went wrong. One part so every game
   * says these things at the same size and in the same colours. TwinTen's
   * notes and errors (its picker, account and together screens).
   *
   * - `tone`: `muted` (the default, `--ui-ink-2`), `ink`, or `error`
   *   (`--ui-danger`, announced at once with `role="alert"`).
   * - `size`: `md` (the label step, 0.85rem) or `sm` (the caption step).
   * - `weight`: `regular` (the default) or `bold` (a note that is a fact:
   *   "Playing as Sam").
   * - `status`: announce a `muted` or `ink` line politely when it changes
   *   (`role="status"`), for a line that appears in answer to something.
   * - `centered`.
   *
   * Test id: `testid`. No default.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly children: Snippet;
    readonly tone?: 'muted' | 'ink' | 'error';
    readonly size?: 'sm' | 'md';
    readonly weight?: 'regular' | 'bold';
    readonly centered?: boolean;
    readonly status?: boolean;
    readonly testid?: string | undefined;
  }
  const {
    children,
    tone = 'muted',
    size = 'md',
    weight = 'regular',
    centered = false,
    status = false,
    testid,
  }: Props = $props();

  const role = $derived(tone === 'error' ? 'alert' : status ? 'status' : undefined);
</script>

<p
  class="ui-note ui-note--{tone} ui-note--{size}"
  class:ui-note--bold={weight === 'bold'}
  class:ui-note--centered={centered}
  {role}
  data-testid={testid}
>
  {@render children()}
</p>

<style>
  .ui-note {
    margin: 0;
    font: var(--ui-text-label);
    font-weight: 400;
    line-height: 1.4;
  }

  .ui-note--sm {
    font-size: 0.78rem;
  }

  .ui-note--bold {
    font-weight: 700;
  }

  .ui-note--muted {
    color: var(--ui-ink-2);
  }

  .ui-note--ink {
    color: var(--ui-ink);
  }

  .ui-note--error {
    color: var(--ui-danger);
  }

  .ui-note--centered {
    text-align: center;
  }
</style>
